/**
 * Ekrany rankingu w modalu gry — patrz templates/modals/_ranking.html.twig.
 *
 * Dwie klasyfikacje, jeden kod: ekrany różnią się wyłącznie wartością `data-ranking-type`
 * ('daily' / 'global'), po której dobiera się endpoint z atrybutów modalu. Każdy trzyma
 * własny numer strony, więc przeskok z rankingu dziennego na generalny i z powrotem nie
 * miesza jednego z drugim.
 *
 * Dane biorą się z tego samego /api/ranking, co strona /ranking, tyle że z uuid zgłoszenia
 * w zapytaniu — po nim API dokłada pole `me`, czyli wiersz gracza. Dzięki temu „Twoje
 * aktualne miejsce" nie wymaga pytania go o numer telefonu.
 *
 * Rozmiar strony jest stały (PER_PAGE): projekt przewiduje piętnaście wierszy i strzałkę
 * obok tabeli, a nie wybór wielkości strony jak na stronie /ranking.
 */

const MODAL = '#modal-game';

/** Piętnaście wierszy na stronę — wartość z projektu, celowo nieregulowana. */
const PER_PAGE = 15;

const EM_DASH = '—';

const numberFormat = new Intl.NumberFormat('pl-PL');

/** Zgłoszenie, którym legitymuje się gracz — bez niego API nie odda wiersza `me`. */
let applicationUuid = null;

const pages = { daily: 1, global: 1 };

// Odpowiedzi bywają spóźnione (szybkie klikanie strzałką), więc każde żądanie dostaje
// numer i przerywa poprzednie: liczy się wyłącznie ostatnie.
const requests = { daily: 0, global: 0 };
const pending = { daily: null, global: null };

function $modal() {
    return $(MODAL);
}

function $screen(type) {
    return $modal().find(`.modal-screen[data-ranking-type="${type}"]`);
}

/**
 * Zapamiętuje zgłoszenie bieżącej rozgrywki i zeruje strony.
 *
 * Wołane ze startGameFlow (assets/js/game/index.js) — modal bywa otwierany kilka razy
 * na jednym załadowaniu strony tylko w dev, ale stan i tak nie ma prawa przeciekać
 * między zgłoszeniami.
 */
export function setRankingApplication(uuid) {
    applicationUuid = uuid ?? null;
    pages.daily = 1;
    pages.global = 1;
}

/** Wejście na ekran rankingu — zawsze od pierwszej strony. */
export function openRanking(type) {
    pages[type] = 1;
    load(type);
}

async function load(type) {
    const $view = $screen(type);
    const id = ++requests[type];

    pending[type]?.abort();
    pending[type] = new AbortController();
    $view.find('[data-role="loader"]').removeClass('d-none');

    try {
        const response = await fetch(url(type), {
            headers: { Accept: 'application/json' },
            signal: pending[type].signal,
        });
        const payload = await response.json();

        // Wyścig: zdążyło wyjść nowsze żądanie, więc ta odpowiedź jest już nieaktualna
        if (id !== requests[type]) {
            return;
        }

        if (!response.ok) {
            fail($view, payload.error === 'no_such_day'
                ? 'Dla tego dnia nie ma jeszcze rankingu.'
                : 'Nie udało się wczytać rankingu. Spróbuj ponownie za chwilę.');

            return;
        }

        render(type, payload);
    } catch (error) {
        if (error.name === 'AbortError' || id !== requests[type]) {
            return;
        }

        fail($view, 'Nie udało się wczytać rankingu. Sprawdź połączenie i spróbuj ponownie.');
    } finally {
        if (id === requests[type]) {
            $view.find('[data-role="loader"]').addClass('d-none');
        }
    }
}

function url(type) {
    const address = new URL($modal().data(`ranking-endpoint-${type}`), window.location.origin);

    address.searchParams.set('page', String(pages[type]));
    address.searchParams.set('perPage', String(PER_PAGE));

    if (applicationUuid) {
        address.searchParams.set('application', applicationUuid);
    }

    return address;
}

function render(type, payload) {
    const $view = $screen(type);
    const rows = payload.data ?? [];

    // Strona spoza zakresu — klasyfikacja liczona jest na żywo, więc mogła się skurczyć
    // między odczytami. Cofamy się na ostatnią istniejącą zamiast pokazywać pustkę.
    if (rows.length === 0 && payload.meta.page > payload.meta.lastPage) {
        pages[type] = payload.meta.lastPage;

        return load(type);
    }

    fill($view.find('[data-role="rows"]'), rows);

    const empty = rows.length === 0;
    $view.find('[data-role="table"]').toggleClass('d-none', empty);
    $view.find('[data-role="message"]')
        .toggleClass('d-none', !empty)
        .text(empty ? 'W tej klasyfikacji nikt jeszcze nie rozegrał gry.' : '');

    // Gracz bywa poza klasyfikacją — gra rozegrana poza dniem konkursu, wykluczenie —
    // i wtedy cały blok znika, zamiast świecić kreskami.
    $view.find('[data-role="me"]').toggleClass('d-none', !payload.me);
    fill($view.find('[data-role="me-row"]'), payload.me ? [payload.me] : []);

    // Strzałkę widać tylko wtedy, gdy jest gdzie iść — wyłączona zajmowałaby miejsce
    // i niczego nie mówiła (tak samo jak paginacja strony /ranking).
    $view.find('[data-role="prev-page"]').toggleClass('d-none', payload.meta.page <= 1);
    $view.find('[data-role="next-page"]').toggleClass('d-none', payload.meta.page >= payload.meta.lastPage);
}

function fail($view, message) {
    fill($view.find('[data-role="rows"]'), []);
    $view.find('[data-role="table"]').addClass('d-none');
    $view.find('[data-role="me"]').addClass('d-none');
    $view.find('[data-role="prev-page"], [data-role="next-page"]').addClass('d-none');
    $view.find('[data-role="message"]').removeClass('d-none').text(message);
}

function fill($tbody, rows) {
    $tbody.each(function () {
        this.replaceChildren(...rows.map(rowElement));
    });
}

function rowElement(row) {
    const tr = document.createElement('tr');

    tr.append(
        placeCell(row),
        // Numery krótsze niż siedem cyfr backend zasłania w całości, a brak numeru oddaje
        // nullem — w obu wypadkach nie ma czego pokazać poza kreską.
        cell(row.phone ?? EM_DASH),
        cell(numberFormat.format(row.score)),
    );

    return tr;
}

function cell(text) {
    const td = document.createElement('td');
    td.textContent = text;

    return td;
}

/** Miejsce, a przy nim plakietka nagrody — API dokłada `prize` tylko w zasięgu puli. */
function placeCell(row) {
    const td = document.createElement('td');
    const place = document.createElement('span');

    place.className = 'modal-screen__standings-place';
    place.textContent = numberFormat.format(row.position);

    if (row.prize) {
        const badge = document.createElement('span');
        badge.className = 'modal-screen__standings-badge';
        badge.textContent = row.prize.name;
        place.append(badge);
    }

    td.append(place);

    return td;
}

function move(button, step) {
    const $view = $(button).closest('.modal-screen');
    const type = $view.data('ranking-type');

    pages[type] = Math.max(1, pages[type] + step);
    load(type);

    // Strzałka wisi w połowie wysokości tabeli, więc po kliknięciu gracz stoi w środku
    // listy — nowa strona ma się zacząć od pierwszego miejsca, nie od ósmego.
    $view.find('.modal-screen__banner')[0]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

$(document).on('click', `${MODAL} [data-role="prev-page"]`, function () {
    move(this, -1);
});

$(document).on('click', `${MODAL} [data-role="next-page"]`, function () {
    move(this, 1);
});
